import React from 'react';
import styled, {keyframes} from 'styled-components';
import {CenteredTextBlock, ImageProps, VerticalSpacer} from '../index';
import {LogoItem} from './Logo';
import {useBreakpointDown} from 'epic-ue-theme';
import {useInView} from 'react-intersection-observer';
import root from 'window-or-global';

const MarqueeAnimation = (width: number) => keyframes`
    0% { left: ${width}px; }
    100% { left: -${width}px; }
`;

const calculateLogoMarqueeWidth = (logosList) => {
    const logoWidthReducer = (totalWidth, item) => {
        const defaultMargin = 48;
        const defaultWidth = 160;

        let logoWidth = item.maxWidth > defaultWidth ? item.maxWidth : defaultWidth;

        const paddingOverride = item.padding > 0 ? item.padding * 2 : 0;
        logoWidth += paddingOverride + defaultMargin;

        return totalWidth + logoWidth;
    };
    return logosList.reduce(logoWidthReducer, 0);
};

const LogoWrap = styled.div`
    margin: 0 auto;
    text-align: center;
    padding: 0;
    width: 100%;
    &.flip {
        ul > li {
            img {
                filter: invert(1);
                transform: translate3d(0, 0, 0);
            }
        }
    }
    ul {
        list-style: none;
        margin: 0 auto;
        padding: 0;
        text-align: center;
        width: 100%;
    }
`;

const Marquee = styled.div<{speed: number; logoListWidth: number; animationState: string}>`
    height: 100px;
    width: 99vw;
    position: relative;
    overflow: hidden;
    margin: 0 auto;

    ul {
        position: absolute;
        width: ${(p) => p.logoListWidth}px;
        transform: translate3d(0, 0, 0);
        &:first-child {
            animation: ${(p) => MarqueeAnimation(p.logoListWidth)} ${(p) => p.speed}s linear
                infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
            animation-play-state: ${(p) => p.animationState};
        }
        &:last-child {
            animation: ${(p) => MarqueeAnimation(p.logoListWidth)} ${(p) => p.speed}s linear
                infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }

            animation-delay: -${(p) => p.speed && p.speed / 2}s;
            animation-play-state: ${(p) => p.animationState};
        }

        list-style: none;
        margin: 0;
        padding: 0;
        text-align: center;
        li {
            float: left;
        }
    }

    &.flip {
        ul > li {
            img {
                filter: invert(1);
                transform: translate3d(0, 0, 0);
            }
        }
    }
`;
export interface LogoListProps {
    items?: ImageProps & {padding?: number; maxWidth?: number}[];
    flipColor?: boolean;
    disableScroll?: boolean;
    title?: string;
    desc?: string;
    disableCarousel?: boolean;
}

const speed = 8;

export const LogoList = (props: LogoListProps): JSX.Element | null => {
    const {inView, ref} = useInView();
    const {flipColor = false, disableScroll = false, title, desc, disableCarousel} = props;
    const items = props.items || [];
    const itemAmount = items.length;
    const logoItems = items.map((logo, i) => <LogoItem key={`logo-item-${i}`} {...logo} />);
    const isSm = useBreakpointDown('sm');

    const logoListWidth = calculateLogoMarqueeWidth(items);

    const calculatedSpeed = itemAmount * speed;
    const renderCarousel = !disableCarousel && !isSm && root.innerWidth < logoListWidth;

    if (!items.length) {
        return null;
    }

    let element = (
        <LogoWrap className={`logoitems ${flipColor ? 'flip' : ''}`}>
            <ul>{logoItems}</ul>
        </LogoWrap>
    );

    if (renderCarousel && !disableScroll) {
        element = (
            <Marquee
                ref={ref}
                animationState={inView ? 'running' : 'paused'}
                className={`logolist ${flipColor ? 'flip' : ''}`}
                logoListWidth={logoListWidth}
                speed={calculatedSpeed}>
                <ul className="marquee-inner" aria-hidden="true">
                    {items.map((logo, i) => (
                        <LogoItem key={`logo-item-${i}`} {...logo} />
                    ))}
                </ul>
                <ul className="marquee-inner" aria-hidden="true">
                    {items.map((logo, i) => (
                        <LogoItem key={`logo-item-${i}-clone`} {...logo} />
                    ))}
                </ul>
            </Marquee>
        );
    }
    return (
        <>
            {title || desc ? (
                <>
                    <CenteredTextBlock headline={title} description={desc} />
                    <VerticalSpacer variant="custom" spacing={4} downSmSpacing={2} />
                </>
            ) : (
                ''
            )}
            {element}
        </>
    );
};
